import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { keyBy } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { flatMap, map, publishReplay, refCount, switchAll, tap } from 'rxjs/operators';
import { deserialize, deserializeArray } from 'santee-dcts';
import { Settings } from '../core';
import { Category, defaultReportSort, Report, ReportsPackage, ReportsReadModel } from '../documents';
import { getReportType } from '../documents/document-type-resolver';
import { Site, InvestigatorSitesStorage } from '../sites';
import { InvestigatorUnreadReportsStorage } from './investigator-unread-reports-storage';

@Injectable()
export class InvestigatorReportsStorage {

    constructor(
        protected settings: Settings,
        protected http: HttpClient,
        private sitesStorage: InvestigatorSitesStorage,
        private unreadReportsRegistry: InvestigatorUnreadReportsStorage,
    ) { }

    public loadReports(category: Observable<Category>, site: Observable<Site>): Observable<Report[]> {
        let reports = combineLatest(site, category)
            .pipe(
                map((s: [Site, Category]) => this.getReports(s[0], s[1])),
                flatMap((x: Observable<ReportsPackage>) => {
                    return of(of(null), x);
                }),
                switchAll(),
                tap(r => this.unreadReportsRegistry.updateUnreads(r)),
                map(r => this.processNewReportsPackage(r)),
                publishReplay(1),
                refCount()
            );

        return reports;
    }

    private processNewReportsPackage(reportsPackage: ReportsPackage) {
        if (!reportsPackage) {
            return null;
        }
        let reps = reportsPackage.reports;
        reps = reps.sort((a, b) => defaultReportSort(a, b));
        return reps;
    }

    private getReportsForCategory(response: Object, category: Category, site: Site = null, allSites: Site[] = []): ReportsPackage {
        const readModel = deserialize(response, ReportsReadModel);

        const reportType = getReportType(category.categoryId);
        const reports = deserializeArray(readModel.reports, reportType);

        const siteById = keyBy(allSites, x => x.siteId);

        reports.forEach((x: Report) => {
            x.category = category;
            x.site = siteById[x.siteId];
        });
        return {
            category: category,
            reports: reports || []
        };
    }

    private getReports(site: Site, category: Category): Observable<ReportsPackage> {
        return this.sitesStorage.loadSites()
            .pipe(
                flatMap((sites: Site[]) => {
                    return this.http.get(`${this.settings.apiUrl}/investigator/site/${encodeURI(site.siteId.toString())}/documents/${category.categoryId}`)
                        .pipe(map((response) => this.getReportsForCategory(response, category, site, sites)));
                }));
    }
}